import { Component, OnInit } from '@angular/core';
import { ChartConfig, ChartDataInterface } from '@app/models/chart-data.model';
import { DrillShiftDetails } from '@app/models/data-collection.model';
import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
HighchartsMore(Highcharts);
import { GenerateChartDataService } from '@app/backend/generate-chart-data.service';
import { BehaviorSubject, combineLatest, debounceTime, forkJoin, of, Subscription, switchMap, tap } from 'rxjs';
import { ChartDataService } from '@app/backend';
import { SiteService } from '@app/app-state/site.service';
import { UserService } from '@app/app-state/user.service';
import { OverviewDataStructure } from '@app/backend/data-structures';
import { ExecutiveChartDataService } from './executive-chart-data.service';
import * as backend from '@app/backend';

interface ChartWrapper {
	options: Highcharts.Options;
	widthClass: string;
}

@Component({
		selector: 'app-executive-overview-dashboard',
		templateUrl: './executive-overview-dashboard.component.html',
		styleUrls: ['./executive-overview-dashboard.component.css'],
		standalone: false
})
export class ExecutiveOverviewDashboardComponent implements OnInit {

	constructor(
		private generateChartDataService: GenerateChartDataService,
		private chartDataService: ChartDataService,
		private siteService: SiteService,
		private userService: UserService,
		private executiveChartDataService: ExecutiveChartDataService,
	) { }

	public selectedTab = 0;
	private domainId: number;
	private subdomainId: number;
	private siteId: number;
	private startDate: string;
	private endDate: string;
	private selectedTimeline: string
	public executiveTitle: string = 'Executive';
	public selectedStartDate: Date | null = null;
	public selectedEndDate: Date | null = null;
	private subscriptions: Subscription = new Subscription();
	public overviewChartOptions: ChartWrapper[] = [];
	public keyMetricsCharts: ChartWrapper[] = [];
	public costsChartOptions: ChartWrapper[] = [];
	public drillData?: ChartDataInterface[];
	public fragmentationData?: ChartDataInterface[];
	public fieldersData?: ChartDataInterface[];
	public totalCostsData?: ChartDataInterface[];
	public operationalCostsData?: ChartDataInterface[];
	public shiftSummary: Record<string, DrillShiftDetails[]>;

	public selectedTab$ = new BehaviorSubject<number>(0);

	selectTab(index: number) {
		this.selectedTab = index;
		this.selectedTab$.next(index);

		switch (index) {
			case 1:
				this.executiveTitle = 'Key Metrics';
				break;
			case 2:
				this.executiveTitle = 'Costs';
				break;
			default:
				this.executiveTitle = 'Executive';
		}
	}

	ngOnInit() {
		this.initDebouncedCostLoader();

		this.subscriptions.add(
			this.siteService.domainId$.subscribe(domainId => {
				this.domainId = domainId;
				if (domainId) this.getReport();
			})
		);
	}

	private tryTriggerLoadData() {
		if (!this.drillData || !this.fragmentationData || !this.fieldersData || !this.totalCostsData || !this.operationalCostsData || !this.shiftSummary) return;
		this.subscriptions.add(this.userService.userUnitMeasurement$.subscribe(() => {
			this.triggerLoadData(this.drillData, this.fragmentationData, this.fieldersData, this.totalCostsData, this.operationalCostsData, this.shiftSummary);
		}));
	}

	private initDebouncedCostLoader() {
		const costTrigger$ = combineLatest([
			this.siteService.selectedTimeline$,
			this.siteService.startDate$,
			this.siteService.endDate$,
			this.siteService.subDomainId$,
			this.siteService.siteId$,
			this.siteService.domainId$,
			this.selectedTab$
		]).pipe(
			debounceTime(100),
			tap(([timeline, start, end, subdomain, site, domain]) => {
				this.selectedTimeline = timeline;
				this.startDate = start;
				this.endDate = end;
				this.subdomainId = subdomain;
				this.siteId = site;
				this.domainId = domain;
			}),
			switchMap(([_, __, ___, ____, _____, domainId]) => {
				if (!domainId) return of({ costs: [], operationalCosts: [] });

				return forkJoin({
					costs: backend.getCacheDrillAndBlastCostsByDomain(domainId),
					operationalCosts: backend.getOperationalInputDataCollection(domainId)
				});
			})
		);

		this.subscriptions.add(
			costTrigger$.subscribe(({ costs, operationalCosts }) => {
				this.totalCostsData = costs;
				this.operationalCostsData = operationalCosts;
				this.tryTriggerLoadData();
			})
		);
	}

	private getReport() {
		this.subscriptions.add(
			forkJoin({
				drill: this.chartDataService.getCacheDrillDetails(),
				fragmentation: this.chartDataService.getCacheFragmentationData(),
				fielder: this.chartDataService.getCacheFielderAnalytics(),
				shiftSummary: backend.getDrillShiftDetailsCollectionView(this.domainId, this.selectedTimeline, this.startDate, this.endDate, this.subdomainId, this.siteId),
			}).subscribe(({ drill, fragmentation, fielder, shiftSummary }) => {
				this.drillData = drill;
				this.fragmentationData = fragmentation;
				this.fieldersData = fielder;
				this.shiftSummary = shiftSummary;
				this.tryTriggerLoadData();
			})
		);
	}

	private getFiltered(data: ChartDataInterface[] | undefined, fallback: ChartDataInterface[] ) {
		return this.generateChartDataService.getFilteredData(
			data || fallback,
			this.domainId, this.subdomainId, this.siteId,
			this.startDate, this.endDate, this.selectedTimeline
		);
	}

	getWidthClass(dataField: string): 'width-small' | 'width-medium' | 'width-large' {
		const narrowSmallFields = ['costs', 'finesPercent', 'totalExplosiveWeight', 'd50', 'truckMaintenanceCost'];
		const narrowMedFields = ['utilization', 'blastmeetingfrag', 'yourNewFieldHere'];

		if (narrowSmallFields.includes(dataField)) {
			return 'width-small';
		} else if (narrowMedFields.includes(dataField)) {
			return 'width-medium';
		} else {
			return 'width-large';
		}
	}

	private loadCharts(data: ChartDataInterface[], configs: ChartConfig[], chartArray: ChartWrapper[]) {
		configs.forEach(config => {
			const chart = this.executiveChartDataService.generateChart(data, config);

			chartArray.push({
				options: chart,
				widthClass: config.widthClass ?? this.getWidthClass(config.chartType)
			});
		});
	}

	private triggerLoadData(
		drillData?: ChartDataInterface[],
		fragmentationData?: ChartDataInterface[],
		fieldersData?: ChartDataInterface[],
		totalCosts?: ChartDataInterface[],
		operationalCosts?: ChartDataInterface[],
	shiftSummaryData?: Record<string, DrillShiftDetails[]>,
	) {
		const costs = this.getFiltered(totalCosts, []);
		const fielder = this.getFiltered(fieldersData, this.generateChartDataService.originalFielderAnalytics);
		const holes = this.getFiltered(drillData, this.generateChartDataService.originalDrillAndBalstData);
		const frag = this.getFiltered(fragmentationData, this.generateChartDataService.originalFragmentationData);
		const operational = this.getFiltered(operationalCosts, []);
		const shiftSummary = this.getFiltered(this.executiveChartDataService.combinedDrillShiftSummaryData(shiftSummaryData), []);

		switch (this.selectedTab) {
			case 0:
				this.overviewChartOptions = [];
				this.loadCharts(costs, OverviewDataStructure.generalOverviewData.costs, this.overviewChartOptions);
				this.loadCharts(fielder, OverviewDataStructure.generalOverviewData.fielder, this.overviewChartOptions);
				this.loadCharts(holes, OverviewDataStructure.generalOverviewData.holes, this.overviewChartOptions);
				this.loadCharts(frag, OverviewDataStructure.generalOverviewData.fragmentation, this.overviewChartOptions);
				this.loadCharts(shiftSummary, OverviewDataStructure.generalOverviewData.shiftSummary, this.overviewChartOptions);
				break;

			case 1:
				this.keyMetricsCharts = [];
				this.loadCharts(frag, OverviewDataStructure.keyMetricsOverviewData.fragmentationPercent, this.keyMetricsCharts);
				this.loadCharts(fielder, OverviewDataStructure.keyMetricsOverviewData.utilization, this.keyMetricsCharts);
				this.loadCharts(operational, OverviewDataStructure.keyMetricsOverviewData.BlastFragTarget, this.keyMetricsCharts);
				break;

			case 2:
				this.costsChartOptions = [];
				this.loadCharts(costs, OverviewDataStructure.costOverviewData.costOverviewDataGraph, this.costsChartOptions);
				this.loadCharts(operational, OverviewDataStructure.costOverviewData.costOperatinalDataGraph, this.costsChartOptions);
				break;
		}
	}

	chartRefs: Highcharts.Chart[] = [];

	onChartInstance(chart: Highcharts.Chart, index: number): void {
		this.chartRefs[index] = chart;
	}

	ngOnDestroy(): void {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	Highcharts: typeof Highcharts = Highcharts;
}
